import React, { useContext, useEffect, useState } from 'react';
import { ResponsiveContext } from '../contexts/ResponsiveContext';
import TocMenu from './toc/TocMenu';
import { useLocation } from '../contexts/useLocation';
import { useSiteMetadataVersions } from '../queries/useSiteMetadataVersions';
import { SearchBoxContext } from '../contexts/SearchBoxContext';
import { useDocTrace } from '../utils/doc-utils';
import { DocOverview } from '../types/docs';
import BreadCrumbList from './breadcrumbs/BreadCrumbList';
import { isSearchScreen } from '../utils/screenRouteChecks';

type Props = {
  children: React.ReactNode;
  title: string | undefined;
  root: DocOverview | undefined;
  id: string | undefined;
  version: string;
  permalink: string;
  categoryVersions: string[];
};

const MainContent = ({ children, title, root, id }: Props) => {
  const location = useLocation();
  const { latestVersion } = useSiteMetadataVersions();
  const { responsiveState, dispatch } = useContext(ResponsiveContext);
  const { setShowSearchResult } = useContext(SearchBoxContext);
  const [tocElements, setTocElements] = useState<Element[]>([]);
  const docTrace = useDocTrace({ docs: root ? [root] : [], id });
  const hasRoot = root !== undefined;
  const backdropToggle = () => {
    if (responsiveState.isSidebarActive) {
      dispatch({ type: 'toggleSidebar' });
    }

    if (responsiveState.isMenuActive) {
      dispatch({ type: 'toggleMenu' });
    }
  };

  function isLandingPage(): boolean {
    return location.pathname === '/';
  }

  const isSearch = isSearchScreen(location.pathname);

  useEffect(() => {
    setTocElements([]);
    const headingElements = Array.from(document.querySelectorAll('h2,h3,h4'));
    setTocElements(headingElements);
  }, [location.pathname]);

  return (
    <div
      className="flex flex-row md:pt-0 shrink dark:bg-very-dark-grey"
    >
      <div
        className={`flex flex-row w-full ${
          isSearch ? 'lg:w-full pb-0' : isLandingPage() ? 'lg:w-full' : 'lg:w-9/12 pb-[10vh] xl:pb-[30vh]'
        } dark:bg-very-dark-grey`}
        onClick={() => setShowSearchResult(false)}
      >
        {(responsiveState.isSidebarActive || responsiveState.isMenuActive) && (
          <div className="absolute w-full z-30 bg-backdrop" onClick={backdropToggle} />
        )}
        <div
          className={`flex flex-col w-full ${isSearch ? 'p-0' : 'pt-1 px-4'} main ${isLandingPage() ? '' : 'overflow-hidden'}`}
        >
          {hasRoot && !isLandingPage() && <BreadCrumbList docTrace={docTrace} latestVersion={latestVersion} id={id} />}
          {title && <h1 className="title">{title}</h1>}
          <div className={`${isLandingPage() || isSearch ? 'pb-0' : 'pb-20'} wrap-break-word`}>{children}</div>
        </div>
      </div>
      {hasRoot && !isLandingPage() && (
        <div className="hidden lg:block lg:w-3/12 dark:bg-very-dark-grey">
          <TocMenu elements={tocElements} />
        </div>
      )}
    </div>
  );
};
export default MainContent;
